import { useRef, useState } from 'react'
import { PatientHistoryType } from 'src/graphql.generated'
import { DEFAULT_INTAKE_NOTE_TTL_IN_HOURS, PatientHistoryFormType } from 'src/participantHistory/constants'
import { useNoteDraftAutosave, useUnsavedNoteDraftPrompt } from 'src/noteDraft/useNoteDraft'
import { clearLocal, readLocal } from './localStore'

type Values = Record<string, unknown>

const slotOf = (uid: string, type: PatientHistoryFormType) => `PatientHistoryForm-${uid}-${type}`
const INTAKE_NOTE_SLOT_TYPES: PatientHistoryFormType[] = [
  PatientHistoryType.IntakeNote,
  PatientHistoryType.IntakeNotePsychiatry,
]
const DRAFT_EXPIRY_HOURS = 24

const expiryOf = (type: PatientHistoryFormType) =>
  INTAKE_NOTE_SLOT_TYPES.includes(type) ? DEFAULT_INTAKE_NOTE_TTL_IN_HOURS : DRAFT_EXPIRY_HOURS

const primed = new Map<string, Values | null>()

const readHistoryDraft = (uid: string, type: PatientHistoryFormType): Values | null => {
  const stored = readLocal<unknown>(slotOf(uid, type), null, { expiresInHours: expiryOf(type) })

  return typeof stored === 'object' && stored !== null && !Array.isArray(stored) ? (stored as Values) : null
}

export const primeRestoredDraft = ({
  uid,
  type,
  value,
}: {
  uid: string
  type: PatientHistoryFormType
  value: Values | null
}) => {
  primed.set(`${uid}:${type}`, value)
}

export const clearHistoryDraft = (uid: string, type: PatientHistoryFormType): void => clearLocal(slotOf(uid, type))

// The parent primes the server draft before this renders. Without that, the browser copy is the fallback.
export const useRestoredDraft = (uid: string, type: PatientHistoryFormType): Values | null =>
  useState(() => {
    const key = `${uid}:${type}`
    if (primed.has(key)) {
      return primed.get(key) ?? null
    }
    return readHistoryDraft(uid, type)
  })[0]

export const useDraftSaver = (
  uid: string,
  type: PatientHistoryFormType,
  value: Values,
  submitting = false
): boolean => {
  const serialized = JSON.stringify(value)
  const opened = useRef(serialized)
  const dirty = serialized !== opened.current

  useNoteDraftAutosave({
    uid,
    type: String(type),
    values: value,
    enabled: dirty && !submitting,
    submitting,
  })
  useUnsavedNoteDraftPrompt({ uid, type: String(type), values: value, dirty })

  return dirty
}
